/**
 * /app/security — Security Centre (cross-trust, per §12).
 *
 * Wired to:
 *   - supabase.auth.mfa.listFactors() — TOTP + phone factor inventory
 *   - supabase.auth.mfa.unenroll({ factorId }) — disable an enrolled factor
 *   - GET  /v1/me/backup-codes[?regenerate=true] — issue/rotate single-use
 *     recovery codes (real; backend mints + hashes, shows plaintext once)
 *   - GET  /v1/auth/sessions — active sessions
 *   - DELETE /v1/auth/sessions/{sessionId} — revoke a session
 *
 * Honest-surface notes (FE-6 / Henry R2-004 — never advertise an unbuilt
 * feature):
 *
 *   - Recovery codes ARE real: the Generate/Regenerate button calls the
 *     backend backup-codes endpoint and shows the plaintext set once in a
 *     modal. Issuance AND regeneration are step-up gated server-side (QA
 *     P0-1, 2026-10-04: a password-only session used to be able to mint the
 *     first set, and a code can strip MFA at recovery). The 403 opens the
 *     global step-up modal, which replays the request; the codes from that
 *     replay arrive in the mutation's state, not in the original call's
 *     promise, so the screen renders from the mutation state.
 *
 *   - Backup email / recovery phone self-service updates have NO endpoint,
 *     so the page does not offer Update buttons that would do nothing — the
 *     Account-recovery card points the user to their admin / support instead.
 *
 *   - Cross-device session enumeration is not available, so the page states
 *     plainly that only the current session is shown rather than promising a
 *     future listing.
 */

import { useEffect, useRef, useState } from 'react';
import { displayPersonName } from '../../lib/format/person-name';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { PASSWORD_MIN_LENGTH, passwordProblem } from '../../lib/password-policy';
import { PasswordRequirements } from '../../components/auth/password-requirements';
import { ScreenShell } from '../../components/screens/screen-shell';
import { Icon } from '../../components/icon';
import { Modal } from '../../components/ui/modal';
import { StateCard } from '../../components/state-card';
import { ConsoleBand } from '../../components/admin/console-band';
import { useToastStore } from '../../lib/toast-store';
import { supabase, createIsolatedAuthClient } from '../../lib/supabase';
import {
  useAuthSessions,
  useRevokeAuthSession,
  useIssueBackupCodes,
  type AuthSessionView,
  type BackupCodesAlreadyIssued,
} from '../../lib/api/auth';
import { ApiError } from '../../lib/api/client';
import {
  usePasskeys,
  useEnrollPasskey,
  useRemovePasskey,
  type PasskeySummary,
} from '../../lib/api/webauthn';
import { isWebAuthnSupported } from '../../lib/webauthn';
import {
  enrollPasskeyFactor,
  isPasskeyFactorSupported,
  passkeyFactorsFrom,
} from '../../lib/mfa-passkey';
import {
  emailCodeEnabledForUser,
  setEmailCodeEnabled,
} from '../../lib/mfa-email-code';
import { mapError } from '../../lib/error-messages';
import { mapSupabaseAuthError } from '../../lib/auth-errors';
import { signOutAndRedirect } from '../../lib/sign-out';
import { useTenantBrandingBySlug } from '../../lib/api/branding-public';
import { resolveTenantSlugFromHost } from '../../lib/tenant';
import { useOperatorMe } from '../../lib/api/operator-identity';
import { useTenantMe } from '../../lib/api/tenants';
import { stewardshipOf } from '../../lib/firm-stewardship';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';

export const Route = createFileRoute('/app/security')({
  component: SecurityScreen,
});

// ── Shared field style ───────────────────────────────────────────────────────

const passwordInputStyle: React.CSSProperties = {
  width: '100%',
  padding: '11px 14px',
  borderRadius: 9,
  border: '1px solid var(--hairline-2)',
  background: 'var(--bg)',
  color: 'var(--ink)',
  fontFamily: 'var(--sans)',
  fontSize: 15,
  outline: 'none',
  boxSizing: 'border-box',
};

// ── MFA factor inventory ─────────────────────────────────────────────────────

interface MfaFactorView {
  id: string;
  friendlyName: string | undefined;
  status: 'verified' | 'unverified';
  /** Supabase's three factor types. `webauthn` arrived with auth-js 2.105.3 —
   *  a passkey enrolled here is a REAL factor that reaches aal2 on its own, not
   *  the self-hosted step-up passkey further down this page. See
   *  lib/mfa-passkey.ts for why both exist. */
  factorType: 'totp' | 'phone' | 'webauthn';
  createdAt: string;
}

function useMfaFactors() {
  return useQuery<MfaFactorView[]>({
    queryKey: ['supabase-mfa-factors'],
    queryFn: async () => {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (error) throw new Error(error.message);
      const totp = (data?.totp ?? []).map<MfaFactorView>((f) => ({
        id: f.id,
        friendlyName: f.friendly_name,
        status: f.status,
        factorType: 'totp',
        createdAt: f.created_at,
      }));
      const phone = (data?.phone ?? []).map<MfaFactorView>((f) => ({
        id: f.id,
        friendlyName: f.friendly_name,
        status: f.status,
        factorType: 'phone',
        createdAt: f.created_at,
      }));
      // Read the webauthn factors off the shared list helper rather than a
      // `data.webauthn` bucket directly: on auth-js 2.105.3 the typed buckets
      // are verified-only while `all` carries everything, and mfa-passkey.ts
      // already reconciles the two (the same trap lib/mfa-totp.ts documents at
      // length for TOTP).
      const passkeys = passkeyFactorsFrom(data).map<MfaFactorView>((f) => ({
        id: f.id,
        friendlyName: f.friendlyName,
        status: 'verified',
        factorType: 'webauthn',
        createdAt: f.createdAt,
      }));
      return [...totp, ...phone, ...passkeys];
    },
  });
}

/** Enrol a Supabase-native passkey factor (one browser gesture; no QR, no
 *  secret). Invalidates the factor list so the card reflects it immediately. */
function useEnrollPasskeyFactor() {
  const qc = useQueryClient();
  return useMutation<Awaited<ReturnType<typeof enrollPasskeyFactor>>, Error, void>({
    mutationFn: () => enrollPasskeyFactor(`Elreign passkey ${new Date().toISOString().slice(0, 10)}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['supabase-mfa-factors'] });
    },
  });
}

// ── Extra email code (opt-in) ────────────────────────────────────────────────
//
// Beno's ask, Paul's ruling 2026-08-18. This is NOT a factor and NOT a
// replacement for one: switching it on means that after the authenticator or
// passkey, a code is also emailed and must be entered. It never raises `aal`
// and never stamps step-up freshness — lib/mfa-email-code.ts carries the full
// argument and the code paths that make that structural rather than promised.

/** The signed-in user's own `user_metadata`, for the opt-in state. */
function useEmailCodePreference() {
  return useQuery<boolean>({
    queryKey: ['mfa-email-code-pref'],
    queryFn: async () => {
      const { data, error } = await supabase.auth.getSession();
      if (error) throw new Error(error.message);
      return emailCodeEnabledForUser(data.session?.user);
    },
  });
}

function useSetEmailCodePreference() {
  const qc = useQueryClient();
  return useMutation<void, Error, { enabled: boolean }>({
    mutationFn: ({ enabled }) => setEmailCodeEnabled(enabled),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['mfa-email-code-pref'] });
    },
  });
}

function useUnenrollMfa() {
  const qc = useQueryClient();
  return useMutation<void, Error, { factorId: string }>({
    mutationFn: async ({ factorId }) => {
      const { error } = await supabase.auth.mfa.unenroll({ factorId });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['supabase-mfa-factors'] });
    },
  });
}

// ── Change password (in-app, FE-6) ───────────────────────────────────────────
//
// Supabase has no "verify current password" primitive on updateUser, so the
// SDK-supported way to confirm the user still knows their existing password is
// to re-authenticate with signInWithPassword before calling
// updateUser({ password }).
//
// The re-auth runs on a THROWAWAY isolated client (createIsolatedAuthClient:
// persistSession:false + isolated storageKey + no auto-refresh / URL parse), so
// the password check never touches the live session. Running it on the main
// client would replace the active session — downgrading a stepped-up (aal2)
// session to aal1 and clobbering the token in every concurrent tab. The actual
// updateUser({ password }) then runs on the MAIN client so the change applies
// to the real session. New-password rules mirror /auth/signup (shared
// password policy in lib/password-policy.ts + confirm-match); error strings
// go through mapSupabaseAuthError.

export function validateNewPassword(
  current: string,
  next: string,
  confirm: string,
): string | null {
  if (!current) return 'Enter your current password to confirm it’s you.';
  // SGN-02: the same four rules the checklist under the field draws, from the
  // same list in lib/password-policy.ts. The message names what is still
  // missing, never "invalid".
  const problem = passwordProblem(next, 'Your new password');
  if (problem !== null) return problem;
  if (next !== confirm) return 'The two new-password fields do not match.';
  if (next === current) {
    return 'Your new password must be different from your current one.';
  }
  return null;
}

class CurrentPasswordError extends Error {}

function useChangePassword() {
  return useMutation<void, Error, { currentPassword: string; newPassword: string }>({
    mutationFn: async ({ currentPassword, newPassword }) => {
      // Read the signed-in email from the session for the re-auth challenge.
      const { data: sessionData } = await supabase.auth.getSession();
      const email = sessionData.session?.user.email;
      if (!email) {
        throw new Error('Your session has expired. Please sign in again.');
      }
      // Re-authenticate on an ISOLATED throwaway client to confirm the current
      // password WITHOUT mutating the live session (no aal2→aal1 downgrade, no
      // cross-tab token swap). A failure here means the current password was
      // wrong (or rate-limited) — surface it against the current-password
      // field, not the new one.
      const reauthClient = createIsolatedAuthClient();
      try {
        const { error: reauthError } = await reauthClient.auth.signInWithPassword({
          email,
          password: currentPassword,
        });
        if (reauthError) {
          throw new CurrentPasswordError(reauthError.message);
        }
      } finally {
        // Drop the ephemeral session LOCALLY so nothing lingers, even though it
        // was never persisted. Scope MUST be 'local': a default/global signOut
        // revokes every refresh token for this user server-side — including the
        // live session we are trying to protect. Best-effort; a failure here
        // must not mask the re-auth result or block the password update.
        await reauthClient.auth.signOut({ scope: 'local' }).catch(() => undefined);
      }
      // Apply the change on the MAIN client so the live session is updated.
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      });
      if (updateError) throw new Error(updateError.message);
    },
  });
}

// ── Formatters ───────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' });
}

function formatUnixSecs(secs: number): string {
  if (!secs) return 'Not on file';
  const d = new Date(secs * 1000);
  if (Number.isNaN(d.getTime())) return 'Not on file';
  return d.toLocaleString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// ── Toggle component ─────────────────────────────────────────────────────────

interface ToggleProps {
  enabled: boolean;
  onToggle: () => void;
  label: string;
  disabled?: boolean;
}

function Toggle({ enabled, onToggle, label, disabled }: ToggleProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      aria-label={label}
      onClick={onToggle}
      disabled={disabled}
      style={{
        width: 36, height: 20, borderRadius: 999, border: 'none', padding: 0,
        background: enabled ? 'var(--primary-700)' : 'var(--surface-2)',
        position: 'relative', cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background 200ms var(--ease-cushion)',
        flexShrink: 0,
        opacity: disabled ? 0.55 : 1,
      }}
    >
      <span style={{
        position: 'absolute',
        top: 3,
        left: enabled ? 18 : 3,
        width: 14, height: 14,
        borderRadius: '50%',
        background: 'white',
        boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
        transition: 'left 200ms var(--ease-cushion)',
      }} />
    </button>
  );
}

// ── Passkeys (biometric step-up) ─────────────────────────────────────────────
//
// Self-hosted WebAuthn (see lib/webauthn.ts): enroll / list / remove passkeys
// that can satisfy the SAME step-up posture as TOTP, via a Face ID / Touch ID /
// Windows Hello gesture. The biometric never leaves the device — only a public
// key reaches Elreign (POPIA: no server-side biometric matching).

function PasskeysCard() {
  const { pushToast } = useToastStore();
  const supported = isWebAuthnSupported();
  const passkeysQ = usePasskeys();
  const enrollMut = useEnrollPasskey();
  const removeMut = useRemovePasskey();

  const [deviceName, setDeviceName] = useState('');
  const [removeTarget, setRemoveTarget] = useState<PasskeySummary | null>(null);

  const passkeys = passkeysQ.data ?? [];

  // Feature deliberately off for this environment (WEBAUTHN_* unset →
  // 503 WEBAUTHN_NOT_CONFIGURED): show an honest muted note instead of an
  // enroll form whose every submit would fail (2026-07-18 sweep finding).
  if ((passkeysQ.error as { code?: string } | null)?.code === 'WEBAUTHN_NOT_CONFIGURED') {
    return (
      <div className="card" style={{ marginBottom: 24 }} data-testid="passkeys-unavailable">
        <div className="eyebrow">Biometric sign-in</div>
        <h3 className="card-title">Passkeys</h3>
        <p style={{ fontSize: 13.5, color: 'var(--ink-2)', margin: 0 }}>
          Biometric sign-in isn&rsquo;t enabled on this environment yet. Your
          authenticator-app codes cover everything in the meantime.
        </p>
      </div>
    );
  }

  async function handleEnroll(e: React.FormEvent) {
    e.preventDefault();
    if (enrollMut.isPending) return;
    const name = deviceName.trim() || 'This device';
    try {
      await enrollMut.mutateAsync({ deviceName: name });
      setDeviceName('');
      pushToast({ kind: 'success', message: 'Passkey added. You can now confirm high-trust actions with it.' });
    } catch (err) {
      // A user cancelling the browser prompt throws — treat that quietly.
      const msg = err instanceof Error ? err.message : '';
      if (/cancel|abort|NotAllowed/i.test(msg)) {
        pushToast({ kind: 'warn', message: 'Passkey setup was cancelled.' });
        return;
      }
      pushToast({ kind: 'error', message: mapError(err, 'generic') });
    }
  }

  async function handleRemoveConfirm() {
    if (!removeTarget) return;
    const target = removeTarget;
    try {
      await removeMut.mutateAsync({ id: target.id });
      pushToast({ kind: 'warn', message: `Passkey “${target.deviceName}” removed.` });
    } catch (err) {
      pushToast({ kind: 'error', message: mapError(err, 'generic') });
    } finally {
      setRemoveTarget(null);
    }
  }

  return (
    <div className="card" style={{ marginBottom: 24 }} data-testid="passkeys-card">
      <div className="card-head" style={{ marginBottom: 20 }}>
        <div>
          <div className="eyebrow">Biometric sign-in</div>
          <h3 className="card-title">Passkeys</h3>
        </div>
        <span style={{
          fontSize: 11.5, fontWeight: 600, padding: '4px 12px', borderRadius: 999,
          background: passkeys.length > 0 ? 'var(--positive-soft)' : 'var(--surface-2)',
          color: passkeys.length > 0 ? 'var(--positive)' : 'var(--ink-3)',
          /* Sentence case (design gate D2/E1, cohesion pass 2026-09-15): the
             text was already sentence case, only the CSS was shouting. */
        }}>
          {passkeysQ.isLoading ? '…' : passkeys.length > 0 ? `${passkeys.length} enrolled` : 'None'}
        </span>
      </div>

      <p style={{ fontSize: 13.5, color: 'var(--ink-3)', lineHeight: 1.6, margin: '0 0 16px' }}>
        Confirm high-trust actions with your fingerprint, Face ID, or Windows
        Hello instead of typing a code. Your biometric never leaves this device.
        Only a passkey is stored. Passkeys work <em>alongside</em> your
        authenticator, which always remains available.
      </p>

      {!supported ? (
        <div style={{ fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.6 }}>
          This browser doesn’t support passkeys. Open Elreign in a browser with
          Face ID / Touch ID / Windows Hello support to enrol one.
        </div>
      ) : (
        <>
          {passkeysQ.isError ? (
            <div style={{ marginBottom: 12 }}>
              <StateCard
                variant="error"
                testId="passkeys-error"
                label="Passkeys"
                headline="Passkeys aren’t available right now."
                detail="Your authenticator still works."
                action={{ label: 'Try again', onClick: () => void passkeysQ.refetch() }}
              />
            </div>
          ) : passkeys.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 18 }}>
              {passkeys.map((pk) => (
                <div
                  key={pk.id}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    gap: 16, padding: '12px 14px', borderRadius: 9,
                    border: '1px solid var(--hairline)', background: 'var(--surface-2)',
                  }}
                >
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink)' }}>{pk.deviceName}</div>
                    <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                      Added {formatDate(pk.createdAt)}
                      {pk.lastUsedAt ? ` · last used ${formatDate(pk.lastUsedAt)}` : ' · not used yet'}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setRemoveTarget(pk)}
                    disabled={removeMut.isPending}
                    style={{ background: 'transparent', color: 'var(--negative)', border: '1px solid var(--negative-soft)', padding: '5px 12px', borderRadius: 7, fontFamily: 'var(--sans)', fontSize: 12.5, fontWeight: 500, cursor: removeMut.isPending ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          ) : passkeysQ.isLoading ? (
            <div style={{ marginBottom: 12 }}>
              <StateCard
                variant="loading"
                testId="passkeys-loading"
                label="Passkeys"
                headline="Reading your enrolled passkeys…"
              />
            </div>
          ) : null}

          <form onSubmit={(e) => void handleEnroll(e)} style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1, minWidth: 220 }}>
              <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink-2)' }}>Name this device (optional)</span>
              <input
                type="text"
                value={deviceName}
                onChange={(e) => setDeviceName(e.target.value.slice(0, 64))}
                placeholder="e.g. MacBook Touch ID"
                disabled={enrollMut.isPending}
                aria-label="Passkey device name"
                style={passwordInputStyle}
              />
            </label>
            <button
              type="submit"
              disabled={enrollMut.isPending}
              style={{ background: 'var(--primary-700)', color: 'var(--surface)', border: 'none', padding: '11px 20px', borderRadius: 9, fontFamily: 'var(--sans)', fontSize: 13.5, fontWeight: 600, cursor: enrollMut.isPending ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}
            >
              {enrollMut.isPending ? 'Follow the prompt…' : 'Add a passkey'}
            </button>
          </form>
        </>
      )}

      {/* Remove confirmation */}
      <Modal
        open={removeTarget !== null}
        onClose={() => setRemoveTarget(null)}
        title="Remove this passkey"
        accentBorder="var(--negative)"
      >
        <p style={{ fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.6, margin: '0 0 4px' }}>
          “{removeTarget?.deviceName}” will no longer be able to confirm high-trust
          actions. You can add it again at any time. Your authenticator is
          unaffected.
        </p>
        <Modal.Footer>
          <button
            type="button"
            onClick={() => setRemoveTarget(null)}
            style={{ background: 'transparent', border: '1px solid var(--hairline)', padding: '9px 18px', borderRadius: 9, fontFamily: 'var(--sans)', fontSize: 13.5, cursor: 'pointer', color: 'var(--ink-2)' }}
          >
            Keep passkey
          </button>
          <button
            type="button"
            onClick={() => void handleRemoveConfirm()}
            disabled={removeMut.isPending}
            style={{ background: 'var(--negative)', color: 'white', border: 'none', padding: '9px 18px', borderRadius: 9, fontFamily: 'var(--sans)', fontSize: 13.5, fontWeight: 600, cursor: removeMut.isPending ? 'wait' : 'pointer' }}
          >
            Remove passkey
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

// ── Main screen ──────────────────────────────────────────────────────────────

function SecurityScreen() {
  const { pushToast } = useToastStore();
  const navigate = useNavigate();

  const factorsQ = useMfaFactors();
  const sessionsQ = useAuthSessions();
  const unenrollMut = useUnenrollMfa();
  const revokeMut = useRevokeAuthSession();
  const backupCodesMut = useIssueBackupCodes();
  const changePasswordMut = useChangePassword();
  // Grader R1 row 8 — the operator's OWN persisted FICA identity (S-1.1
  // operator-pinned: never the act-as client's). Populated by the onboarding
  // identity confirm (PATCH /v1/users/me).
  const meQ = useOperatorMe();
  const meUser = meQ.data?.user;

  // White-label: surface the FIRM's own support email (from its branding
  // overlay) on the account-recovery card, instead of an Elreign address.
  const firmSlug =
    typeof window !== 'undefined'
      ? resolveTenantSlugFromHost(window.location.host, new URLSearchParams(window.location.search))
      : null;
  const firmSupportEmail = useTenantBrandingBySlug(firmSlug).data?.branding.supportEmail ?? null;
  const stewardship = stewardshipOf(useTenantMe().data);

  const factors = factorsQ.data ?? [];
  const verifiedTotp = factors.find((f) => f.factorType === 'totp' && f.status === 'verified');
  const verifiedPhone = factors.find((f) => f.factorType === 'phone' && f.status === 'verified');
  // A verified passkey factor is a COMPLETE second factor on its own — a user
  // with one and no authenticator is fully protected, so it counts toward
  // "MFA active" exactly as TOTP does.
  const verifiedPasskeyFactor = factors.find(
    (f) => f.factorType === 'webauthn' && f.status === 'verified',
  );
  const mfaEnabled = !!verifiedTotp || !!verifiedPhone || !!verifiedPasskeyFactor;
  const passkeyFactorOffered = isPasskeyFactorSupported();
  const enrollPasskeyFactorMut = useEnrollPasskeyFactor();
  const emailCodeQ = useEmailCodePreference();
  const setEmailCodeMut = useSetEmailCodePreference();

  const sessions: AuthSessionView[] = sessionsQ.data?.sessions ?? [];

  const [revokeTarget, setRevokeTarget] = useState<AuthSessionView | null>(null);
  const [disableMfaTarget, setDisableMfaTarget] = useState<MfaFactorView | null>(null);
  // Plaintext backup codes are held in component state only long enough to
  // show them once in the modal; cleared on close. Never persisted.
  const [shownCodes, setShownCodes] = useState<string[] | null>(null);
  // When the backend says codes already exist (409) we surface the remaining
  // count + a "Regenerate replaces them" prompt rather than the plaintext.
  const [alreadyIssued, setAlreadyIssued] = useState<BackupCodesAlreadyIssued | null>(null);

  // Change-password form state. The inline error is shown in the card; success
  // clears the fields and toasts. Never logs or echoes any password value.
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);

  async function handleChangePassword(e: React.FormEvent) {
    e.preventDefault();
    if (changePasswordMut.isPending) return;
    const validationError = validateNewPassword(
      currentPassword,
      newPassword,
      confirmPassword,
    );
    if (validationError) {
      setPasswordError(validationError);
      return;
    }
    setPasswordError(null);
    try {
      await changePasswordMut.mutateAsync({ currentPassword, newPassword });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      pushToast({ kind: 'success', message: 'Password updated.' });
    } catch (err) {
      setPasswordError(
        mapSupabaseAuthError(err instanceof Error ? err.message : ''),
      );
    }
  }

  // ── RECOVERY CODES RENDER FROM THE MUTATION'S STATE (QA P0-1) ──────────
  // Issuing codes now needs a fresh step-up. The first call answers 403, the
  // global StepUpInterceptor opens the modal, and the modal REPLAYS the
  // mutation once the user has confirmed. That replay's result lands in the
  // mutation state, never in the promise `requestBackupCodes` awaited (that
  // one already rejected with the 403). Reading only the promise meant a
  // replayed regeneration minted a fresh set, invalidated the old one, and
  // showed the user nothing: codes nobody would ever see. So both outcomes are
  // read from the state, and each error is handled once.
  const handledBackupCodesError = useRef<unknown>(null);
  useEffect(() => {
    const codes = backupCodesMut.data?.codes;
    if (codes && codes.length > 0) {
      setAlreadyIssued(null);
      setShownCodes(codes);
    }
  }, [backupCodesMut.data]);
  useEffect(() => {
    const e = backupCodesMut.error;
    if (!e || handledBackupCodesError.current === e) return;
    handledBackupCodesError.current = e;
    // The step-up modal is open and will replay; it reports its own failures.
    if (e instanceof ApiError && e.isStepUpRequired) return;
    // 409 ALREADY_ISSUED on a non-regenerate call → offer regeneration.
    if (e instanceof ApiError && e.status === 409 && e.code === 'ALREADY_ISSUED') {
      setAlreadyIssued((e.body ?? {}) as BackupCodesAlreadyIssued);
      return;
    }
    // Anything else, including 409 BACKUP_CODES_CHANGED (another tab issued a
    // set at the same moment), says so in the server's own words.
    const serverMessage =
      e instanceof ApiError && e.body && typeof e.body === 'object' &&
      typeof (e.body as Record<string, unknown>)['message'] === 'string'
        ? ((e.body as Record<string, unknown>)['message'] as string)
        : null;
    pushToast({
      kind: 'error',
      message: e instanceof ApiError && e.status === 409 && serverMessage ? serverMessage : mapError(e, 'generic'),
    });
  }, [backupCodesMut.error, pushToast]);

  function requestBackupCodes(regenerate: boolean) {
    setAlreadyIssued(null);
    handledBackupCodesError.current = null;
    // Outcomes are rendered by the two effects above, whichever way they
    // arrive (directly, or through the step-up replay).
    backupCodesMut.mutate({ regenerate });
  }

  function closeBackupCodesModal() {
    setShownCodes(null);
    setAlreadyIssued(null);
    backupCodesMut.reset();
  }

  async function handleRevokeConfirm() {
    if (!revokeTarget) return;
    const target = revokeTarget;
    try {
      await revokeMut.mutateAsync({ sessionId: target.id });
      // Revoking the current session ends it server-side — finish with the
      // shared sign-out sequence (act-as clear → local signOut → /auth/login)
      // rather than leaving the dead session on screen. Toast first so the
      // redirect can't drop it.
      if (target.current) {
        pushToast({ kind: 'success', message: 'Signed out of this device.' });
        await signOutAndRedirect(navigate);
      } else {
        pushToast({ kind: 'success', message: 'Session revoked.' });
      }
    } catch (e) {
      pushToast({ kind: 'error', message: mapError(e, 'generic') });
    } finally {
      setRevokeTarget(null);
    }
  }

  async function handleEnrollPasskeyFactor() {
    if (enrollPasskeyFactorMut.isPending) return;
    try {
      const outcome = await enrollPasskeyFactorMut.mutateAsync();
      if (outcome.status === 'ok') {
        pushToast({
          kind: 'success',
          message: 'Passkey added. You can now sign in with it instead of an authenticator code.',
        });
        return;
      }
      // A cancelled ceremony is the user changing their mind, not a failure.
      if (outcome.status === 'cancelled') {
        pushToast({ kind: 'warn', message: 'Passkey setup was cancelled.' });
        return;
      }
      pushToast({ kind: 'error', message: outcome.message });
    } catch (e) {
      pushToast({ kind: 'error', message: mapError(e, 'generic') });
    }
  }

  async function handleToggleEmailCode() {
    if (setEmailCodeMut.isPending) return;
    const next = !(emailCodeQ.data ?? false);
    try {
      await setEmailCodeMut.mutateAsync({ enabled: next });
      pushToast({
        kind: next ? 'success' : 'warn',
        message: next
          ? 'Extra email code switched on. From your next sign-in you’ll enter an emailed code as well as your authenticator or passkey.'
          : 'Extra email code switched off. Your authenticator or passkey still protects your account.',
      });
    } catch (e) {
      pushToast({ kind: 'error', message: mapError(e, 'generic') });
    }
  }

  async function handleDisableMfaConfirm() {
    if (!disableMfaTarget) return;
    const target = disableMfaTarget;
    try {
      await unenrollMut.mutateAsync({ factorId: target.id });
      pushToast({
        kind: 'warn',
        message:
          target.factorType === 'totp'
            ? 'TOTP authenticator disabled. Your account is now less secure. Re-enrol soon.'
            : target.factorType === 'webauthn'
              ? 'Passkey removed. If it was your only second factor, enrol another one now.'
              : 'Phone factor disabled. Your account is now less secure. Re-enrol soon.',
      });
    } catch (e) {
      pushToast({ kind: 'error', message: mapError(e, 'generic') });
    } finally {
      setDisableMfaTarget(null);
    }
  }

  return (
    <ScreenShell
      activeNav="security"
      eyebrow="Account security"
      title="Security Centre"
      sub="Multi-factor authentication (MFA), active sessions, and POPIA data-rights shortcut"
    >
      <ConsoleBand id="authentication" title="Authentication">
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
        {/* MFA status */}
        <div className="card">
          <div className="card-head" style={{ marginBottom: 20 }}>
            <div>
              <div className="eyebrow">Authentication</div>
              <h3 className="card-title">Multi-factor</h3>
            </div>
            <span style={{
              fontSize: 11.5, fontWeight: 600, padding: '4px 12px', borderRadius: 999,
              background: mfaEnabled ? 'var(--positive-soft)' : 'var(--negative-soft)',
              color: mfaEnabled ? 'var(--positive)' : 'var(--negative)',
              // Sentence case (design gate D2/E1, cohesion pass 2026-09-15).
            }}>
              {factorsQ.isLoading ? '…' : mfaEnabled ? 'Active' : 'Disabled'}
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {factorsQ.isLoading ? (
              <StateCard
                variant="loading"
                testId="mfa-loading"
                label="Multi-factor"
                headline="Reading your multi-factor authentication factors…"
              />
            ) : factorsQ.isError ? (
              <StateCard
                variant="error"
                testId="mfa-error"
                label="Multi-factor"
                headline={mapError(factorsQ.error, 'load')}
                detail="Your enrolment is safe."
                action={{ label: 'Try again', onClick: () => void factorsQ.refetch() }}
              />
            ) : (
              <>
                {/* TOTP row */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink)' }}>
                      TOTP authenticator
                    </div>
                    <div style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>
                      {verifiedTotp ? `Enrolled ${formatDate(verifiedTotp.createdAt)}` : 'Not enrolled'}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {verifiedTotp ? (
                      <Toggle
                        enabled
                        label="TOTP authenticator"
                        disabled={unenrollMut.isPending}
                        onToggle={() => setDisableMfaTarget(verifiedTotp)}
                      />
                    ) : (
                      <Link
                        to="/auth/mfa-enroll"
                        style={{ background: 'var(--primary-700)', color: 'var(--surface)', padding: '6px 14px', borderRadius: 8, fontFamily: 'var(--sans)', fontSize: 13, fontWeight: 500, textDecoration: 'none' }}
                      >
                        Enrol
                      </Link>
                    )}
                  </div>
                </div>

                {/* Passkey row — a REAL Supabase `webauthn` factor, i.e. an
                    ALTERNATIVE to the authenticator above, not an add-on. A
                    user with one enrolled can sign in with a fingerprint or
                    Face ID and never open an authenticator app again. (The
                    separate "Passkeys" card further down is the self-hosted
                    step-up credential — different mechanism, both wanted; see
                    lib/mfa-passkey.ts.) Hidden entirely when the browser or
                    the SDK can't do it, rather than offering a button whose
                    every press would fail. */}
                {(passkeyFactorOffered || verifiedPasskeyFactor) && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink)' }}>
                        Passkey
                      </div>
                      <div style={{ fontSize: 12.5, color: 'var(--ink-3)', lineHeight: 1.55 }}>
                        {verifiedPasskeyFactor
                          ? `Enrolled ${formatDate(verifiedPasskeyFactor.createdAt)}. Sign in with Face ID, Touch ID or Windows Hello instead of a code`
                          : 'Sign in with Face ID, Touch ID or Windows Hello instead of an authenticator code'}
                      </div>
                    </div>
                    {verifiedPasskeyFactor ? (
                      <Toggle
                        enabled
                        label="Passkey factor"
                        disabled={unenrollMut.isPending}
                        onToggle={() => setDisableMfaTarget(verifiedPasskeyFactor)}
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => void handleEnrollPasskeyFactor()}
                        disabled={enrollPasskeyFactorMut.isPending}
                        data-testid="enrol-passkey-factor"
                        style={{ background: 'var(--primary-700)', color: 'var(--surface)', border: 'none', padding: '6px 14px', borderRadius: 8, fontFamily: 'var(--sans)', fontSize: 13, fontWeight: 500, cursor: enrollPasskeyFactorMut.isPending ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}
                      >
                        {enrollPasskeyFactorMut.isPending ? 'Follow the prompt…' : 'Enrol'}
                      </button>
                    )}
                  </div>
                )}

                {/* Phone row (only shown if enrolled) */}
                {verifiedPhone && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink)' }}>SMS factor</div>
                      <div style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>
                        Enrolled {formatDate(verifiedPhone.createdAt)}
                      </div>
                    </div>
                    <Toggle
                      enabled
                      label="SMS factor"
                      disabled={unenrollMut.isPending}
                      onToggle={() => setDisableMfaTarget(verifiedPhone)}
                    />
                  </div>
                )}

                {/* Recovery codes row — wired to GET /v1/me/backup-codes.
                    FE-6: this is a real capability, not a stub. The button
                    issues codes (first time) or regenerates them; the plaintext
                    set is shown once in the modal below. */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink)' }}>
                      Recovery codes
                    </div>
                    <div style={{ fontSize: 12.5, color: 'var(--ink-3)', lineHeight: 1.55 }}>
                      Single-use codes that let you sign in if you lose your
                      authenticator. Shown once when generated. Store them safely.
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => requestBackupCodes(false)}
                    disabled={backupCodesMut.isPending}
                    style={{ background: 'transparent', color: 'var(--primary-700)', border: '1px solid var(--primary-200)', padding: '5px 12px', borderRadius: 7, fontFamily: 'var(--sans)', fontSize: 12.5, fontWeight: 500, cursor: backupCodesMut.isPending ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}
                  >
                    {backupCodesMut.isPending ? 'Working…' : 'Generate codes'}
                  </button>
                </div>

                {/* Extra email code — OPT-IN, and deliberately worded so nobody
                    mistakes it for a factor. It is an ADDITIONAL step after the
                    authenticator or passkey; it cannot replace one, and it does
                    not raise your session's assurance level on its own. Only
                    offered once a real factor exists — advertising it to a user
                    with no second factor would invite exactly the "email is my
                    2FA" misreading this whole design refuses. */}
                {mfaEnabled && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink)' }}>
                        Extra email code
                      </div>
                      <div style={{ fontSize: 12.5, color: 'var(--ink-3)', lineHeight: 1.55 }}>
                        Belt and braces: on top of your authenticator or passkey,
                        we email you a code to enter as well. It is an extra
                        step, never a replacement. You will always still need
                        your authenticator or passkey.
                      </div>
                    </div>
                    <Toggle
                      enabled={emailCodeQ.data ?? false}
                      label="Extra email code"
                      disabled={emailCodeQ.isLoading || setEmailCodeMut.isPending}
                      onToggle={() => void handleToggleEmailCode()}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Recovery info */}
        <div className="card" data-testid="recovery-card">
          <div className="card-head" style={{ marginBottom: 20 }}>
            <div>
              <div className="eyebrow">Recovery</div>
              <h3 className="card-title">Account recovery</h3>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* 2026-05-20 (H-16 / F02-P-063 / Henry's CRITICAL): the previous
                three "not yet wired" rows are removed pending real wiring.
                Showing them was worse than not showing them — auditors read
                three honest "not yet" disclosures on the Security page and
                downgrade trust. When the recovery-email update endpoint
                lands, restore this card with a real Update button. */}
            {/* Who recovery goes through depends on whether a firm stewards
                this workspace (NEW-R12-SELFMANAGED-FIRM-COPY: a client with no
                firm was sent to "your trust admin firm"). A recovery request
                is approved by Elreign support, super admin only
                (backend `admin-recovery-requests-approve.ts`). */}
            <div style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--ink-2)' }}>
              {stewardship === 'firm' ? (
                <>
                  Account recovery is administered by your trust admin firm. To
                  update your recovery email or phone, contact your administrator
                  {firmSupportEmail ? (
                    <> or write to <a href={`mailto:${firmSupportEmail}`} style={{ color: 'var(--ink)' }}>{firmSupportEmail}</a></>
                  ) : null}.{' '}
                </>
              ) : stewardship === 'self' ? (
                <>
                  You manage this account yourself, so its recovery does not go
                  through a firm. With a backup code you can reset your
                  authenticator yourself on the{' '}
                  <Link to="/auth/recovery" style={{ color: 'var(--ink)' }}>account recovery page</Link>;
                  without one, that page sends a request to Elreign support, who
                  confirm who you are before restoring access.{' '}
                </>
              ) : (
                <>
                  If you lose access, the{' '}
                  <Link to="/auth/recovery" style={{ color: 'var(--ink)' }}>account recovery page</Link>{' '}
                  takes you through getting it back.{' '}
                </>
              )}
              Single-use backup codes can be generated below once you have
              an authenticator enrolled.
            </div>
          </div>
        </div>
      </div>

      {/* Passkeys (biometric step-up) — self-hosted WebAuthn, an ADDITIONAL
          factor alongside TOTP. */}
      <PasskeysCard />
      </ConsoleBand>

      {/* Identity on file (grader R1 row 8) — the persistent home of the
          confirmed FICA identity. Before this card existed the extracted
          name / SA ID / DOB / address were shown ONCE on the transient
          onboarding card and then nowhere ever again. Own data shown to its
          owner (operator-pinned read); honest empty state when nothing has
          been confirmed yet. */}
      <ConsoleBand id="identity" title="Identity">
      <div className="card" style={{ marginBottom: 24 }} data-testid="identity-on-file">
        <div className="card-head" style={{ marginBottom: 20 }}>
          <div>
            <div className="eyebrow">Identity</div>
            <h3 className="card-title">Identity on file</h3>
          </div>
          {meUser?.identityConfirmedAt ? (
            <span style={{
              fontSize: 11.5, fontWeight: 600, padding: '4px 12px', borderRadius: 999,
              background: 'var(--positive-soft)', color: 'var(--positive)',
              // Sentence case (design gate D2/E1, cohesion pass 2026-09-15).
            }}>
              Confirmed {formatDate(meUser.identityConfirmedAt)}
            </span>
          ) : null}
        </div>
        {meQ.isLoading ? (
          <p style={{ color: 'var(--ink-3)', fontSize: 13 }} aria-live="polite">
            Loading your identity record&hellip;
          </p>
        ) : meQ.isError ? (
          <p role="alert" style={{ color: 'var(--negative)', fontSize: 13, lineHeight: 1.55 }}>
            Couldn&rsquo;t load your identity record. {mapError(meQ.error, 'load')}
          </p>
        ) : meUser?.idNumber || meUser?.dateOfBirth || meUser?.residentialAddress ? (
          <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 24, rowGap: 10, margin: 0, fontSize: 14 }}>
            <dt style={{ color: 'var(--ink-3)', fontWeight: 500 }}>Full name</dt>
            <dd style={{ margin: 0, color: 'var(--ink)' }}>{displayPersonName(meUser.displayName) || 'Not recorded'}</dd>
            <dt style={{ color: 'var(--ink-3)', fontWeight: 500 }}>ID number</dt>
            <dd style={{ margin: 0, color: 'var(--ink)', fontFamily: 'var(--mono)' }}>{meUser.idNumber ?? 'None'}</dd>
            <dt style={{ color: 'var(--ink-3)', fontWeight: 500 }}>Date of birth</dt>
            <dd style={{ margin: 0, color: 'var(--ink)' }}>{meUser.dateOfBirth ?? 'Not on file'}</dd>
            <dt style={{ color: 'var(--ink-3)', fontWeight: 500 }}>Residential address</dt>
            <dd style={{ margin: 0, color: 'var(--ink)' }}>{meUser.residentialAddress ?? 'Not recorded'}</dd>
          </dl>
        ) : (
          <p style={{ color: 'var(--ink-3)', fontSize: 13.5, lineHeight: 1.6, margin: 0 }}>
            No confirmed identity on file yet. Your identity details are
            captured when you confirm them during onboarding.
          </p>
        )}
      </div>
      </ConsoleBand>

      {/* Change password — in-app self-service (FE-6). Re-authenticates with
          the current password before updating, so a stolen-but-unlocked
          session can't silently change the password. */}
      <ConsoleBand id="password" title="Password">
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-head" style={{ marginBottom: 20 }}>
          <div>
            <div className="eyebrow">Password</div>
            <h3 className="card-title">Change password</h3>
          </div>
        </div>
        <form
          onSubmit={(e) => void handleChangePassword(e)}
          style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 420 }}
        >
          <label htmlFor="current-password" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink-2)' }}>Current password</span>
            <input
              id="current-password"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
              required
              disabled={changePasswordMut.isPending}
              aria-invalid={passwordError !== null}
              aria-describedby={passwordError !== null ? 'change-password-error' : undefined}
              style={passwordInputStyle}
            />
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label htmlFor="new-password" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink-2)' }}>New password</span>
              <input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                required
                minLength={PASSWORD_MIN_LENGTH}
                disabled={changePasswordMut.isPending}
                aria-invalid={passwordError !== null}
                aria-describedby={
                  passwordError !== null
                    ? 'new-password-requirements change-password-error'
                    : 'new-password-requirements'
                }
                style={passwordInputStyle}
              />
            </label>
            {/* SGN-02: the flat "At least 12 characters." hint that used to sit
                here understated the policy and could not show progress. */}
            <PasswordRequirements value={newPassword} id="new-password-requirements" />
          </div>
          <label htmlFor="confirm-password" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink-2)' }}>Confirm new password</span>
            <input
              id="confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              required
              minLength={PASSWORD_MIN_LENGTH}
              disabled={changePasswordMut.isPending}
              aria-invalid={passwordError !== null}
              aria-describedby={passwordError !== null ? 'change-password-error' : undefined}
              style={passwordInputStyle}
            />
          </label>
          {passwordError !== null && (
            <div
              id="change-password-error"
              role="alert"
              aria-live="polite"
              style={{
                padding: '10px 12px',
                borderRadius: 8,
                background: 'var(--negative-soft)',
                border: '1px solid var(--negative-soft)',
                color: 'var(--negative)',
                fontSize: 13,
                lineHeight: 1.45,
              }}
            >
              {passwordError}
            </div>
          )}
          <div>
            <button
              type="submit"
              disabled={changePasswordMut.isPending}
              style={{
                background: 'var(--primary-700)',
                color: 'var(--surface)',
                border: 'none',
                padding: '10px 20px',
                borderRadius: 9,
                fontFamily: 'var(--sans)',
                fontSize: 13.5,
                fontWeight: 600,
                cursor: changePasswordMut.isPending ? 'wait' : 'pointer',
              }}
            >
              {changePasswordMut.isPending ? 'Updating…' : 'Update password'}
            </button>
          </div>
        </form>
      </div>
      </ConsoleBand>

      {/* Active sessions */}
      <ConsoleBand id="sessions" title="Sessions">
      <div className="card" style={{ padding: 0, marginBottom: 24 }}>
        <div style={{ padding: '22px 28px', borderBottom: '1px solid var(--hairline)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 className="serif-heading" style={{ fontWeight: 400, fontSize: 22, margin: 0 }}>Active sessions</h3>
          <span className="card-meta">
            {sessionsQ.isLoading
              ? '…'
              : `${sessions.length} device${sessions.length !== 1 ? 's' : ''}`}
          </span>
        </div>

        {sessionsQ.isLoading ? (
          <div style={{ padding: '20px 28px' }}>
            <StateCard
              variant="loading"
              testId="sessions-loading"
              label="Active sessions"
              headline="Reading your active sessions across your devices…"
            />
          </div>
        ) : sessionsQ.isError ? (
          <div style={{ padding: '20px 28px' }}>
            <StateCard
              variant="error"
              testId="sessions-error"
              label="Active sessions"
              headline={mapError(sessionsQ.error, 'load')}
              detail="Your account is safe."
              action={{ label: 'Try again', onClick: () => void sessionsQ.refetch() }}
            />
          </div>
        ) : sessions.length === 0 ? (
          <div style={{ padding: '20px 28px', fontSize: 13, color: 'var(--ink-3)' }}>
            No active sessions on record. You appear to be signed out
            everywhere except this device.
          </div>
        ) : (
          <>
            <table className="holdings-tbl" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ paddingLeft: 28 }}>Session</th>
                  <th>Issued</th>
                  <th>Expires</th>
                  <th style={{ paddingRight: 28 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => (
                  <tr key={s.id}>
                    <td style={{ paddingLeft: 28 }}>
                      <div className="holding-name">
                        {s.current ? 'Current session' : `Session ${s.id.slice(0, 8)}…`}
                      </div>
                      {s.current && (
                        <div style={{ fontSize: 11.5, color: 'var(--positive)', fontWeight: 600 }}>
                          You
                        </div>
                      )}
                    </td>
                    <td>
                      <span className="mono" style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>
                        {formatUnixSecs(s.issuedAt)}
                      </span>
                    </td>
                    <td>
                      <span className="mono" style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>
                        {formatUnixSecs(s.expiresAt)}
                      </span>
                    </td>
                    <td style={{ paddingRight: 28 }}>
                      <button
                        type="button"
                        onClick={() => setRevokeTarget(s)}
                        disabled={revokeMut.isPending}
                        style={{ background: 'transparent', color: 'var(--negative)', border: '1px solid var(--negative-soft)', padding: '5px 12px', borderRadius: 7, fontFamily: 'var(--sans)', fontSize: 12.5, fontWeight: 500, cursor: revokeMut.isPending ? 'wait' : 'pointer' }}
                      >
                        {s.current ? 'Sign out' : 'Revoke session'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {/* FE-6: state the scope factually rather than advertising an
                unbuilt cross-device listing. When only the current session is
                on record, say so plainly. */}
            {sessions.length === 1 && sessions[0]?.current && (
              <div style={{ padding: '12px 28px', borderTop: '1px solid var(--hairline)', fontSize: 12.5, color: 'var(--ink-3)', lineHeight: 1.55 }}>
                <strong style={{ color: 'var(--ink-2)' }}>Note:</strong> this shows the
                device you are signed in on now. If you sign out here, you can sign back
                in at any time.
              </div>
            )}
          </>
        )}
      </div>
      </ConsoleBand>

      {/* DSAR shortcut */}
      <div style={{ padding: '20px 24px', background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: 'var(--r-md)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink)', marginBottom: 4 }}>Your data rights (POPIA)</div>
          <div style={{ fontSize: 13.5, color: 'var(--ink-3)', lineHeight: 1.6 }}>Request a copy of your data, correct what we hold, or initiate account deletion under Protection of Personal Information Act (POPIA) s24.</div>
        </div>
        <Link
          to="/app/privacy"
          style={{ background: 'transparent', color: 'var(--primary-700)', border: '1px solid var(--primary-200)', padding: '9px 18px', borderRadius: 9, fontFamily: 'var(--sans)', fontSize: 13.5, fontWeight: 500, textDecoration: 'none', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6 }}
        >
          <Icon name="shield" size={14} /> Open Privacy Centre
        </Link>
      </div>

      {/* ── Modals ── */}

      {/* Session revoke confirmation */}
      <Modal
        open={revokeTarget !== null}
        onClose={() => setRevokeTarget(null)}
        title={revokeTarget?.current ? 'Sign out of this device' : 'Revoke this session'}
      >
        <p style={{ fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.6, margin: '0 0 4px' }}>
          {revokeTarget?.current
            ? 'You will be signed out and returned to the login screen. You can sign back in at any time.'
            : 'The other device will be signed out immediately. It will need to authenticate again before it can access Elreign.'}
        </p>
        <Modal.Footer>
          <button
            type="button"
            onClick={() => setRevokeTarget(null)}
            style={{ background: 'transparent', border: '1px solid var(--hairline)', padding: '9px 18px', borderRadius: 9, fontFamily: 'var(--sans)', fontSize: 13.5, cursor: 'pointer', color: 'var(--ink-2)' }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleRevokeConfirm()}
            disabled={revokeMut.isPending}
            style={{ background: 'var(--negative)', color: 'white', border: 'none', padding: '9px 18px', borderRadius: 9, fontFamily: 'var(--sans)', fontSize: 13.5, fontWeight: 600, cursor: revokeMut.isPending ? 'wait' : 'pointer' }}
          >
            {revokeTarget?.current ? 'Sign out' : 'Revoke session'}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Disable MFA confirmation */}
      <Modal
        open={disableMfaTarget !== null}
        onClose={() => setDisableMfaTarget(null)}
        title={
          disableMfaTarget?.factorType === 'totp'
            ? 'Disable TOTP authenticator'
            : disableMfaTarget?.factorType === 'webauthn'
              ? 'Remove this passkey'
              : 'Disable phone factor'
        }
        accentBorder="var(--negative)"
      >
        <p style={{ fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.6, margin: '0 0 4px' }}>
          {disableMfaTarget?.factorType === 'totp'
            ? 'Disabling your TOTP authenticator reduces your account security. You can re-enrol a new authenticator at any time, but you will need access to the device to do so.'
            : disableMfaTarget?.factorType === 'webauthn'
              ? 'Removing this passkey means you will sign in with your authenticator code instead. If it is your only second factor, your account will be left without one until you enrol another. Do that straight away.'
              : 'Disabling this phone factor reduces your account security. You can re-enrol a phone number from your account settings.'}
        </p>
        <Modal.Footer>
          <button
            type="button"
            onClick={() => setDisableMfaTarget(null)}
            style={{ background: 'transparent', border: '1px solid var(--hairline)', padding: '9px 18px', borderRadius: 9, fontFamily: 'var(--sans)', fontSize: 13.5, cursor: 'pointer', color: 'var(--ink-2)' }}
          >
            Keep enabled
          </button>
          <button
            type="button"
            onClick={() => void handleDisableMfaConfirm()}
            disabled={unenrollMut.isPending}
            style={{ background: 'var(--negative)', color: 'white', border: 'none', padding: '9px 18px', borderRadius: 9, fontFamily: 'var(--sans)', fontSize: 13.5, fontWeight: 600, cursor: unenrollMut.isPending ? 'wait' : 'pointer' }}
          >
            Disable anyway
          </button>
        </Modal.Footer>
      </Modal>

      {/* Backup codes — shown once after issuance, or the already-issued prompt */}
      <Modal
        open={shownCodes !== null || alreadyIssued !== null}
        onClose={closeBackupCodesModal}
        title={shownCodes !== null ? 'Your recovery codes' : 'Recovery codes already issued'}
      >
        {shownCodes !== null ? (
          <>
            <p style={{ fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.6, margin: '0 0 12px' }}>
              Save these somewhere safe. A password manager is ideal. Each code
              works once, and <strong>this is the only time they are shown</strong>.
            </p>
            <div
              role="list"
              aria-label="Recovery codes"
              style={{
                display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8,
                padding: 14, borderRadius: 8, background: 'var(--surface-2)',
                border: '1px solid var(--hairline)',
              }}
            >
              {shownCodes.map((c) => (
                <span
                  key={c}
                  role="listitem"
                  className="mono"
                  style={{ fontSize: 13.5, color: 'var(--ink)', letterSpacing: '0.04em' }}
                >
                  {c}
                </span>
              ))}
            </div>
          </>
        ) : (
          <p style={{ fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.6, margin: 0 }}>
            You have already generated recovery codes
            {typeof alreadyIssued?.remainingCount === 'number'
              ? `, with ${alreadyIssued.remainingCount} unused ${alreadyIssued.remainingCount === 1 ? 'code' : 'codes'} remaining`
              : ''}
            . Regenerating issues a fresh set and <strong>invalidates the old ones</strong>.
            You will be asked to confirm with your authenticator.
          </p>
        )}
        <Modal.Footer>
          <button
            type="button"
            onClick={closeBackupCodesModal}
            style={{ background: 'transparent', border: '1px solid var(--hairline)', padding: '9px 18px', borderRadius: 9, fontFamily: 'var(--sans)', fontSize: 13.5, cursor: 'pointer', color: 'var(--ink-2)' }}
          >
            {shownCodes !== null ? 'Done' : 'Cancel'}
          </button>
          {shownCodes === null && (
            <button
              type="button"
              onClick={() => requestBackupCodes(true)}
              disabled={backupCodesMut.isPending}
              style={{ background: 'var(--primary-700)', color: 'var(--surface)', border: 'none', padding: '9px 18px', borderRadius: 9, fontFamily: 'var(--sans)', fontSize: 13.5, fontWeight: 600, cursor: backupCodesMut.isPending ? 'wait' : 'pointer' }}
            >
              {backupCodesMut.isPending ? 'Regenerating…' : 'Regenerate codes'}
            </button>
          )}
        </Modal.Footer>
      </Modal>
    </ScreenShell>
  );
}
